import { anyObject, anyReallyAny, asAnyObject, getProp, setProp } from "./any.tools";
import { isNumeric } from "./number.tools";

export { anyReallyAny } from "./any.tools";

/** Type used to specify what a json path can be build from. Used in functions to retrieve properties from an object. May be used for setting properties in an object */
type JSON_PATH = string | (string | number)[];

/** RequireOnlyOne<{ finish: UnixTimestamp, duration: Second }> is equivalent to { finish: UnixTimestamp } | { duration: Second } */
export type RequireOnlyOne<T, Keys extends keyof T = keyof T> =
    Pick<T, Exclude<keyof T, Keys>>
    & {
        [K in Keys]-?:
        Required<Pick<T, K>>
        & Partial<Record<Exclude<Keys, K>, undefined>>
    }[Keys];

/** 
 * Ensures that passed string is a key of T at compilation time with little overhead. 
 * @returns key
 */
export function keyof<T>(key: keyof T): string {
    return <string>key;
}

/** 
 * This function does "nothing", but typescript ensures that value is correctly typed
 * @returns value
 */
export function is<T>(value: T): T {
    return value;
}

/**
 * @returns a clone of the array
 */
export function cloneArray<T>(arr: T[]): T[] {
    if (arr == null) { return arr; }
    const clone = Object.assign([], arr);
    return clone;
}

/**
 * Create a new object and put object.keys and values (references) in the new object
 * By default, if the value is an array (at root level only) it does not copy the reference but clones the array
 * This does NOT perform a 'deep clone'
 * Limitation : does not clone the object prototype
 * @param object the object to clone
 * @param params
 * @param params.keepArrayReferences if set to true, arrays will not be cloned (the reference will be kept and if you perform clone.arr.push(something) original.arr will be impacted)
 * @param params.fieldsToOmit if given, fields in list won't be cloned in returned object
 */
export function clone<K extends keyof T, T>(object: T, params?: { keepArrayReferences?: boolean, fieldsToOmit?: K[] }): T {
    if (object == null || typeof object !== "object") { return object; }// Handle other types than "object" (note: array, records, function,... are object in javascript)

    if (Array.isArray(object)) {
        if (params?.keepArrayReferences) {
            return object.slice() as unknown as T;// Clone with object references into the new array 
        } else {
            return object.map(obj => clone(obj)) as unknown as T;// Clone array and remove all references
        }
    }
    else if (object instanceof Set) {
        if (params?.keepArrayReferences) {
            return new Set(object) as unknown as T;// Clone with object references into the new set 
        } else {
            return new Set(Array.from(object).map(obj => clone(obj))) as unknown as T;// Clone set and remove all references
        }

    }
    const clonedObject = {} as T;
    for (const key in object as T) {
        if (params?.fieldsToOmit?.includes(key as unknown as K)) { continue; }
        if (!params?.keepArrayReferences && (Array.isArray(object[key]) || object[key] instanceof Set)) {
            // Clone the Array or Set using clone function
            // Don't pass fieldsToOmit parameter, not the same fields between the object and the iterable
            clonedObject[key] = clone(object[key], { keepArrayReferences: params?.keepArrayReferences });
        } else {
            clonedObject[key] = object[key];
        }
    }
    return clonedObject;
}

/**
 * Return true if (o1 and o2 are undefined) or (o1 === o2) or (o1[index] === o2[index])
 * Objects are equal only if they have the same reference, they won't be if they are cloned/deep cloned
 * BGS : we don't handle arrays that contain arrays for now
 */
export function equalOrBothNullObjectOrArray(o1: unknown | null | undefined, o2: unknown | null | undefined): boolean {
    if (o1 == null && o2 == null) { return true; }// ==null => undefined or null
    if (o1 == null || o2 == null) { return false; }// ==null => undefined or null
    if (o1 === o2) { return true; }
    if (Array.isArray(o1) && Array.isArray(o2)) {
        if (o1.length !== o2.length) { return false; }
        for (let i = 0; i < o1.length; i++) {
            if (o1[i] !== o2[i]) { return false; }// BGS : we don't handle arrays that contain arrays for now
        }
        return true;
    }
    return false;
}

/**
 * return true if (o1 and o2 are null or undefined) or (o1[key] === o2[key] for each passed key)
 */
export function matchingKeysOrBothNull<T>(o1: T, o2: T, ...keys: (keyof T)[]): boolean {
    if (o1 == null && o2 == null) { return true; }// ==null => undefined or null
    if (o1 == null || o2 == null) { return false; }// ==null => undefined or null
    return _matchingKeys(o1, o2, ...keys);
}

/**
 * return true if (o1 and o2 are not null) and (o1[key] === o2[key] for each passed key)
 */
export function matchingKeysAndNotNull<T>(o1: T, o2: T, ...keys: (keyof T)[]): boolean {
    if (o1 == null || o2 == null) { return false; } // ==null => undefined or null
    return _matchingKeys(o1, o2, ...keys);
}

/**
 * return true if (o1[key] === o2[key] for each passed key)
 */
function _matchingKeys<T>(o1: T, o2: T, ...keys: (keyof T)[]): boolean {
    if (!keys || !keys.length) { return o1 === o2; }
    for (const key of keys) {
        if (o1[key] !== o2[key]) {
            return false;
        }
    }
    return true;
}

/**
 * compare fields one after one while result is 0, 
 * you can insert "-" before a key to revert comparison order, 
 * you can insert "#" before a key to try to convert the values to numbers
 * you can insert "@" before a key to compare using localeCompare with sensitivity:"base"
 * 
 * compareFields(o1,o2,["name","-","startDate","other"]) => is equivalent to SQL "ORDER BY name ASC, startDate DESC, other ASC"
 * compareFields(o1,o2,["name","#","numberFieldStoredAsString","other"]) will try to convert numberFieldStoredAsString as number before comparison
 * fields can be a key of T or a function (o : T) => unknown
 */
export function compareFields<T>(o1: T | null | undefined, o2: T | null | undefined, ...fields: (ItemPropertyOrFunction<T, unknown> | "-" | "#" | "@")[]): number {
    if (o1 === o2) { return 0; }
    // undefined is lower than any value
    if (o1 === undefined) { return -1; }
    if (o2 === undefined) { return 1; }
    // null is lower than any value except undefined
    if (o1 === null) { return -1; }
    if (o2 === null) { return 1; }

    let signum = 1;
    let treatAsNumbersIfPossible = false;
    let isLocaleCompare = false;
    for (const field of fields) {
        if (field === "-") {
            signum = -1;
            continue;
        }
        if (field === "#") {
            treatAsNumbersIfPossible = true;
            continue;
        }
        if (field === "@") {
            isLocaleCompare = true;
            continue;
        }
        let o1FieldValue = getItemPOFValue(o1, field);
        let o2FieldValue = getItemPOFValue(o2, field);

        if (treatAsNumbersIfPossible) {
            // -- Check if both values are number
            if (isNumeric(o1FieldValue) && isNumeric(o2FieldValue)) {
                o1FieldValue = Number(o1FieldValue);
                o2FieldValue = Number(o2FieldValue);
            }
        }
        const cmp = signum * (isLocaleCompare ? localeCompare(o1FieldValue, o2FieldValue) : compare(o1FieldValue, o2FieldValue));
        if (cmp !== 0) { return cmp; }
        signum = 1;
        treatAsNumbersIfPossible = false;
        isLocaleCompare = false;
    }
    return 0;
}

/**
 * sort the provided list using compareFields function
 * @see compareFields
 * @returns the reference to the original list that was sorted
 */
export function sortFields<T>(list: T[], ...fields: (ItemPropertyOrFunction<T, unknown> | "-" | "#" | "@")[]): T[] {
    return list.sort((o1, o2) => compareFields(o1, o2, ...fields));
}

/**
 * Get the first element in the list sorted according to fields.
 * If a function is passed as a field, it will only be called if needed, and only once per item.
 * Sorting behavior is the same as @see compareFields.
 * In the end, if multiple items of the list have the same ranking, the first one will be returned.
 * 
 * This method is supposed to be as fast or faster than sortFields + getFirst, because it will
 * only evaluate your getter functions on the necessary items.
 * 
 * @returns The first item or null if the list is empty.
 */
export function getFirstInSortedList<T>(list: T[], ...fields: (ItemPropertyOrFunction<T, unknown> | "-" | "#" | "@")[]): T | null {
    // -- Shortcuts for deterministic cases --
    if (list.length === 0) {
        return null;
    } else if (list.length === 1) {
        return list[0];
    }

    let remainingItems = list;
    let signum = 1;
    let treatAsNumbersIfPossible = false;
    let isLocaleCompare = false;
    for (const field of fields) {
        if (field === "-") {
            signum = -1;
            continue;
        }
        if (field === "#") {
            treatAsNumbersIfPossible = true;
            continue;
        }
        if (field === "@") {
            isLocaleCompare = true;
            continue;
        }
        // -- Sort according to field --
        let bestItems: T[] = [];
        let bestValue: unknown = undefined;

        // -- Only keep the best items --
        for (let i = 0; i < remainingItems.length; i++) {
            const item = remainingItems[i];
            let fieldValue = getItemPOFValue(item, field);
            if (treatAsNumbersIfPossible && isNumeric(fieldValue)) {
                // -- Check if both values are number
                fieldValue = Number(fieldValue);
            }
            // No need to compare if it is the first item, we return a negative value to initialize the best value
            const cmp = i === 0 ? -1 : signum * (isLocaleCompare ? localeCompare(fieldValue, bestValue) : compare(fieldValue, bestValue));
            if (cmp < 0) {
                // First item or we found a betterValue
                bestItems = [item];
                bestValue = fieldValue;
            } else if (cmp === 0) {
                // Item's value is equal to the best value
                bestItems.push(item);
            } else {
                // Worst value, ignore the item
            }
        }

        // -- Reset decorators --
        signum = 1;
        treatAsNumbersIfPossible = false;
        isLocaleCompare = false;

        // -- Prepare for the next field --
        if (bestItems.length === 0) {
            throw new Error("Illegal state. There should always be at least one best item");
        } else if (bestItems.length === 1) {
            // Shortcut, if there is only one item left, it will always be the best
            return bestItems[0];
        } else {
            // There is still many items to compare
            remainingItems = bestItems;
        }
    }

    if (remainingItems.length === 0) {
        throw new Error("Illegal state. There should always be at least one best item at the end");
    } else {
        // There is no more fields to compare, send the first one
        return remainingItems[0];
    }
}

/** 
 * using anyReallyAny because we can really compare any kind of value 
 * 
 * * in compare : undefined is lower than null and null is lower than any value
 * * note that even if the compare function considers undefined is lower, if you use compare in the Array.sort function
 * * undefined will always be pushed after other values
 * * see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort#description
 */
export function compare(o1: anyReallyAny, o2: anyReallyAny): number {
    if (o1 === o2) { return 0; }

    // undefined is lower than any value
    if (o1 === undefined) { return -1; }
    if (o2 === undefined) { return 1; }
    // null is lower than any value except undefined
    if (o1 === null) { return -1; }
    if (o2 === null) { return 1; }
    return o1 < o2 ? -1 : 1;
}

/** 
 * Default collator used to enables language-sensitive string comparison    
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Collator
 */
let defaultCollator: Intl.Collator | undefined;
/** Returns the default collator and init it if not initialize */
export function getDefaultCollator(): Intl.Collator {
    if (defaultCollator == null) {
        defaultCollator = new Intl.Collator(undefined, {
            sensitivity: "base",
            usage: "sort"
        });
    }
    return defaultCollator;
}
/** 
 * Compares objects as strings using options but faster specially for large arrays to compare if collator is given.
 * Default options are { sensitivity: "base" }
 * using anyReallyAny because we can really compare any kind of value 
 * Example :
 * ```
 * const collator = new Intl.Collator(undefined, { sensitivity: "base" });
 * localeCompare(o1, o2, collator);
 * ```
 * We use an unique collator to speed up the process instead of creating a new one on each compare.
 * See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/localeCompare.
 * 
 * in compare : undefined is lower than null and null is lower than any value
 * note that even if the compare function considers undefined is lower, if you use compare in the Array.sort function
 * undefined will always be pushed after other values
 * see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort#description
 */
export function localeCompare(o1: anyReallyAny, o2: anyReallyAny, collator?: Intl.Collator): number {
    if (o1 === o2) { return 0; }

    // undefined is lower than any value
    if (o1 === undefined) { return -1; }
    if (o2 === undefined) { return 1; }
    // null is lower than any value except undefined
    if (o1 === null) { return -1; }
    if (o2 === null) { return 1; }
    const collatorToUse = collator ?? getDefaultCollator();
    return collatorToUse.compare(o1, o2);
}

/** 
 * Compare given values as number
 * A cast is applied before compare if given value is a valid numeric value
 * Using anyReallyAny because we can really compare any kind of value 
 */
export function compareAsNumber(o1: anyReallyAny, o2: anyReallyAny): number {
    if (o1 === o2) { return 0; }
    if (o1 == null) { return -1; }
    if (o2 == null) { return 1; }
    if (!isNumeric(o1) && !isNumeric(o2)) { return 0; }// Neither of values aren't numeric, don't compare
    if (!isNumeric(o1)) { return -1; }
    if (!isNumeric(o2)) { return 1; }

    return Number(o1) < Number(o2) ? -1 : 1;
}

/** using anyReallyAny because we can really compare any kind of value */
export function compareReverse(o1: anyReallyAny, o2: anyReallyAny): number {
    return -compare(o1, o2);
}

/**
 * Set the value of a property (given as a string like aaa.bbb.ccc or an array) of an object
 * 
 * @param obj Any the object
 * @param jsonPath String|Array the path of the property (lika aaa.bbb.ccc)
 * @param value Any the new value
 * @returns Any the new value 
 */
export function setValueFromJSONPath(obj: anyReallyAny, jsonPath: anyReallyAny, value: anyReallyAny): anyReallyAny {
    if (typeof jsonPath == "string") {
        return setValueFromJSONPath(obj, jsonPath.split("."), value);
    }
    else if (jsonPath.length === 1 && value !== undefined) {
        return obj[jsonPath[0]] = value;
    }
    else if (jsonPath.length === 0) {
        return obj;
    }
    else {
        return setValueFromJSONPath(obj[jsonPath[0]], jsonPath.slice(1), value);
    }
}

/**
 * Get the value of a property (given as a string like aaa.bbb.ccc) of an object
 * 
 * @param obj Any the object
 * @param jsonPath string or (string | number)[]. The path of the property. 
 *                 If it's a string, accepts property access format : "aaa.bbb.ccc". 
 *                 If it's a String[], jsonPath must be formatted like this example : ["aaa", "bbb", "ccc"] 
 * @returns Any the value. There are special cases though.
 *                         Returns obj if given path is an empty list or an empty string
 *                         Returns undefined in given cases : path was inconsistent, 
 *                                                            one of the properties in path was undefined (might be the case for options for example)
 */
export function getValueFromJSONPath(obj: anyReallyAny, jsonPath: JSON_PATH): anyReallyAny {
    // -- Prepare JSON path array from passed parameter --
    let jsonPathArray: (string | number)[];

    if (typeof jsonPath === "string") {
        if (!jsonPath.length) { // meaning that given path was an empty string
            jsonPathArray = [];
        }
        else {
            jsonPathArray = jsonPath.split(".");
        }
    }
    else if (!Array.isArray(jsonPath)) {
        throw "Invalid Parameter";
    }
    else { // meaning jsonPath is an array
        jsonPathArray = jsonPath;
    }

    // -- Go through the JSON --
    if (jsonPathArray.length === 0) {
        // Shortcut to avoid the reduce call.
        return obj;
    }
    return jsonPathArray.reduce((obj: anyReallyAny, key: string | number) => {
        if (!obj) { return undefined; }
        return obj[key];
    }, obj);
}

/**
 * Compare objects deep equality
 * TO remove once datamodel rewritten
 * 
 * @deprecated
 */
export function deepEqual(o1: anyReallyAny, o2: anyReallyAny, visited = new WeakMap()): boolean {
    // Reference check
    if (o1 === o2) { return true; }

    // Type object check (an array is an object)
    if (!isObject(o1) || !isObject(o2)) { return false; }

    // To avoid infinite loop, check if the current node has already been visited
    if (visited.has(o1) && visited.get(o1) === o2) { return true; }
    visited.set(o1, o2);

    const keys1 = Object.keys(o1);
    const keys2 = Object.keys(o2);
    if (keys1.length !== keys2.length) { return false; }

    for (const key of keys1) {
        // Check if the key exists in o2
        if (!Object.prototype.hasOwnProperty.call(o2, key)) { return false; }

        // Recursive call of deepEqual with both objects value
        if (!deepEqual(o1[key], o2[key], visited)) { return false; }
    }
    return true;
}

/**
 * Check if a variable is an object
 * 
 * @deprecated remove with  deepEqual
 */
function isObject(o: anyReallyAny): boolean {
    return o !== null && typeof o === "object";
}

/**
 * This function does nothing but calling it ensures that the class was loaded
 */
export function ensureLoaded(...loadedClass: unknown[]): void {

}

/** 
 * Utility function to perform a static check, useful to check that you have treated every subclass of a class when you use type predicates
 */
export function assertIsNever<T>(value: never): T | undefined {
    return undefined;
}

export type TGetterFunction<I, T> = (item: I) => T | undefined;
export type TSetterFunction<I, T> = (item: I, value: T | undefined) => boolean;
export type ItemPropertyOrFunction<I, T> = keyof I | TGetterFunction<I, T>;
export type ItemPropertyOrGetSet<I, T> = keyof I | { getter: TGetterFunction<I, T>, setter: TSetterFunction<I, T> };
export type ItemValueOrFunction<I, T> = T | TGetterFunction<I, T> | undefined;
export type ValueOrFunction<T> = T | (() => T | undefined) | undefined;

/**
 * return vof or vof()
 */
export function getVOFValue<T>(vof: ValueOrFunction<T>): T | undefined {
    if (vof == null) { return undefined; }
    if (typeof vof === "function") {
        return (<() => T>vof)();
    }
    return vof;
}

/**
 * return vof or vof(item)
 */
export function getItemVOFValue<I, T>(item: I, vof: ItemValueOrFunction<I, T>, options?: { continueWithNullItem: boolean }): T | undefined {
    if (item == null && !options?.continueWithNullItem || vof == null) { return undefined; }
    if (typeof vof === "function") {
        return (<TGetterFunction<I, T>>vof)(item);
    }
    return vof;
}

/**
 * return item.pof or pof(item)
 */
export function getItemPOFValue<I, T>(item: I | undefined, pof: ItemPropertyOrFunction<I, T>): T | undefined {
    if (item == null || pof == null) { return undefined; }
    if (typeof pof == "string") {
        return getProp(item, pof);
    }
    return (<TGetterFunction<I, T>>pof)(item);
}

/**
 * return item.pogs
 */
export function getItemPOGSValue<I, T>(item: I | undefined, pogs: ItemPropertyOrGetSet<I, T>): T | undefined {
    if (item == null || pogs == null) { return undefined; }
    if (typeof pogs == "string") {
        return getProp(item, pogs);
    }
    const getter = getProp(asAnyObject(pogs), "getter");
    if (getter) {
        return (<TGetterFunction<I, T>>getter)(item);
    } else {
        console.error("no getter found for", pogs);
    }
    return undefined;
}

/**
 * item.pogs=value
 * @returns true if value affected and changed
 */
export function setItemPOGSValue<I, T>(item: I | undefined, pogs: ItemPropertyOrGetSet<I, T>, value: T): boolean {
    if (item == null || pogs == null) { return false; }
    if (typeof pogs === "string") {
        const changed = getProp(item, pogs) !== value;
        setProp(item, pogs, value);
        return changed;
    }

    const setter = getProp(asAnyObject(pogs), "setter");
    if (setter) {
        return (<TSetterFunction<I, T>>setter)(item, value);
    } else {
        console.error("no setter found for", pogs);
    }
    return false;
}

/**
 * returns true if object is an empty object => {}.
 * @param object object to check
 */
export function isEmptyObject(object: unknown): boolean {
    let result = false;
    if (object) {
        result = Object.keys(object as anyReallyAny).length === 0 && (object as anyReallyAny).constructor === Object;
    }
    return result;
}

/** ShapeOf descriptor */
export type ShapeOf<T> = Record<keyof T, anyReallyAny>

/**
 * Check if two interfaces share same keys (strict checking)
 * Usage:
 * type AssertionCheck = AssertKeysEqual<Interface1, Interface2>
 * => Produce an error at compilation time, if some (Interface1, Interface2) keys differs
 */
export type AssertKeysEqual<X extends ShapeOf<Y>, Y extends ShapeOf<X>> = never;


/** Use to require at least one of optional properties 
 * https://stackoverflow.com/questions/40510611/typescript-interface-require-one-of-two-properties-to-exist
 */
export type RequireAtLeastOne<T, Keys extends keyof T = keyof T> =
    Pick<T, Exclude<keyof T, Keys>>
    & {
        [K in Keys]-?: Required<Pick<T, K>> & Partial<Pick<T, Exclude<Keys, K>>>
    }[Keys];

/** Compiler hack to disable type inference (cf. https://stackoverflow.com/a/56688073).
 * It is useful if you want to impose the user of function to explicit the Generic type of the function:
 * @example
 * function myFunc<T = never>(t: NoInfer<T>): void { ... }
 * const t: Type = {...};
 * myFunc<Type>(t); // OK
 * myFunc(t); // does not compile
 */
export type NoInfer<T> = [T][T extends anyReallyAny ? 0 : never];

/**
 * Return true if there is a value not null and not undefined
 *
 * @param object Object
 * @returns {*}  {boolean}
 */
export function isValue(object: unknown): boolean {
    return object != null;
}

/**
 * Invert the keys and values of an object. The values must be serializable.
 * If obj in parameters isn't an object, return it
 *
 * @param obj to invert
 * @returns obj with inverted <keys,values> or object itself if not defined or not a serializable object
 */
export function invert<T extends anyObject>(obj: T): T {
    const result = {};
    // If obj isn't defined or isn't an object, return it
    if (obj == null || typeof obj !== "object") { return obj; }

    const keys = Object.keys(obj);
    for (let i = 0, length = keys.length; i < length; i++) {
        setProp(result, obj[keys[i]], keys[i]);
    }
    return result as unknown as T;
}

/**
 * This replacer can be used with JSON.stringify(object, onlyOwnPropertiesReplacer)
 * It only keeps Own Properties of the objects (it excludes properties inherited from prototype but keep non-enumerable properties)
 * It is useful to serialize objects such as Error
 */
export function onlyOwnPropertiesReplacer(key: string, value: anyReallyAny): Record<string, unknown> {
    if (value != null && typeof value === "object") {
        const result = {};
        Object.getOwnPropertyNames(value).forEach((key) => {
            setProp(result, key, value[key]);
        });
        return result;
    } else {
        return value;
    }
}

/** 
 * Only keep Own properties of value (it excludes properties inherited from prototype but keep non-enumerable properties) 
 * It is useful to prepare objects such as Error for serialization
 */
export function recursiveOnlyOwnProperties(value: anyReallyAny, seen?: Set<anyReallyAny>): Record<string, unknown> {
    if (value == null || typeof value != "object") { return value; }
    seen ??= new Set();
    const result = {};
    for (const key of Object.getOwnPropertyNames(value)) {
        const v = value[key];
        if (seen.has(v)) {
            // prevent infinite loop
            continue;
        }
        setProp(result, key, recursiveOnlyOwnProperties(v, seen));
    }
    return result;
}

/**
 * Return if element if first or second GenericType
 * @param e element we want to test Type generic
 * @param attributes List of attributes of e we can test to prove type
 * FIXME not explicit enough => add a test in order to have an example of its usage
 * @returns 
 */
export function isGenericType<T extends UnionT, UnionT extends anyObject>(e: UnionT, attributes: (keyof T)[]): e is T {
    let isGenericType = true;
    // GenericType must be an object to accept in operator
    if (typeof e === "object" && e != null) {
        const objToTest = e as T;
        for (const a of attributes) {

            if (a == null) {
                break;
            }

            if (!(a in objToTest)) {
                isGenericType = false;
                break;
            }
        }
    } else {
        isGenericType = false;
    }
    return isGenericType;
}

/**
 * dest[key] = src[key];
 * Typescript can't infer dest[key] and src[key] are the same type if you do this directly, hence the need for this function with generics.
 * NB: it's not a deep copy or anything fancy, just a simple assignment (by ref).
 * @param param0 destructured params
 * @param param0.src source object
 * @param param0.dest destination object
 * @param param0.key index of the property to assign
 */
export function copyObjectField<T, K extends keyof T>({ src, dest, key }: { src: T, dest: T, key: K }): void {
    dest[key] = src[key];
}

/**
 * A strongly-typed version of `Object.keys()`.
 * This is useful since `Object.keys()` always returns an array of strings. 
 * This function returns a strongly-typed array of the keys of the given object.
 * Used to avoid:
 * "Element implicitly has an 'any' type because expression of type
 * 'O' can't be used to index type 'object'"
 * @example
 * ```
 * const stronglyTypedItems = objectKeys({a: 1, b: 2, c: 3}); // => Array<'a' | 'b' | 'c'>
 * const untypedItems = Object.keys(items); // => Array<string>
 * ```
 * @param obj to loop into keys
 * @param options
 * @param options.useOnlyStringKeys don't try to convert numeric keys to Number keys
 * @returns keys of object with typing
 */
export function objectKeys<O extends anyObject>(obj: O, options?: { useOnlyStringKeys?: boolean }): Array<keyof O> {
    return Object.keys(obj).map(key => {
        if (options?.useOnlyStringKeys) { return key; }
        const numberKey = Number(key);
        const typedKey = String(numberKey) === key ? numberKey : key;
        return typedKey;
    }) as Array<keyof O>;
}

/**
 * A strongly-typed version of `Object.values()`.
 * This is useful since `Object.values()` always returns an array of `Array<unknown>`.
 * This function returns a strongly-typed array of the entries of the given object.
 * Used to avoid:
 * "Element implicitly has an 'any' type because expression of type
 *  'O' can't be used to index type 'object'"
 * @example
    ```
    const stronglyTypedEntries = objectValues({a: 1, b: 2, c: 3});
    //=> Array<number>
    const untypedEntries = Object.values(items);
    //=> Array<unknown>
    ```
 * @param obj to loop into keys
 * @returns keys of object with typing
 */
export function objectValues<KeyO extends keyof O, O extends anyObject>(obj: O): Array<O[KeyO]> {
    return Object.values(obj) as (O[KeyO])[];
}

/**
 * A strongly-typed version of `Object.entries()`.
 * This is useful since `Object.entries()` always returns an array of `Array<[string, T]>`.
 * @example
 *   ```
 *   const stronglyTypedEntries = objectEntries({a: 1, b: 2, c: 3});
 *   => Array<['a' | 'b' | 'c', number]>
 *   const untypedEntries = Object.entries(items);
 *   => Array<[string, number]>
 *   ```
 * @param obj to loop into entries
 * @param options
 * @param options.useOnlyStringKeys don't try to convert numeric keys to Number keys
 * @returns [keys, values] of object with typing
 */
export function objectEntries<KeyO extends keyof O, O extends anyObject>(
    obj: O,
    options?: { useOnlyStringKeys?: boolean }
): Array<[KeyO, O[KeyO]]> {
    return Object.entries(obj).map(([key, value]) => {
        if (options?.useOnlyStringKeys) { return key; }
        const numberKey = Number(key);
        const typedKey = String(numberKey) === key ? numberKey : key;
        return [typedKey, value];
    }) as Array<[KeyO, O[KeyO]]>;
}

/**
 * Call JSON.stringify on a value unless it is already a string
 */
export function stringifyIfNeeded(v: anyReallyAny): string {
    if (typeof v === "string") {
        return v;
    }
    return JSON.stringify(v);
}
